import moment from 'moment';
import React, {useMemo, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {ScrollView, Text, TouchableOpacity, View} from 'react-native';
import Icon from '@react-native-vector-icons/fontawesome';
import {useSelector} from 'react-redux';
import {ShowToast} from '../Common/common';
import BTComboBox from '../Components/BTComboBox';
import {
  useGetPORunningOrdersQuery,
  useInsertPOEOrderReadyMutation,
  useUpdatePOEOrderReadyMutation,
} from '../redux/services/poeorderApi';
import {useGetPOMKitchensQuery} from '../redux/services/pomkitchenApi';
import {RootState} from '../redux/store';
import styles from '../styles/Styles';

import T from '../Components/T';
import { tp } from '../utils/translateText';
interface OrderItem {
  od_srid: number;
  od_prod: number;
  od_descr: string;
  od_instruct: string;
  od_qty: string;
}

interface OrderHeader {
  or_id: number;
  or_no: string;
  or_date: string;
  or_outlet: string;
  or_area: string;
  or_table: string;
  or_pax: number;
  or_waiter: string;
  od_kitchen: string;
  od_printer: string;
  or_readyid: number;
  or_ready: string;
  or_ordertime: string;
  or_readytime: string;
}

interface GroupedOrder {
  header: OrderHeader;
  items: OrderItem[];
}

// Assume this interface represents the flat order structure you receive
interface FlatOrder extends OrderHeader, OrderItem {}

const groupOrders = (orders: any[]): GroupedOrder[] => {
  const grouped: {[key: string]: GroupedOrder} = {};

  orders[0]?.forEach((order: FlatOrder) => {
    const headerKey = `${order.or_id}_${order.or_no}_${order.or_date}_${order.or_outlet}_${order.or_area}_${order.or_table}_${order.or_waiter}_${order.od_kitchen}`;
    if (!grouped[headerKey]) {
      grouped[headerKey] = {
        header: {
          or_id: order.or_id,
          or_no: order.or_no,
          or_date: moment(order.or_date).format('DD-MM HH:mm'),
          or_outlet: order.or_outlet,
          or_area: order.or_area,
          or_table: order.or_table,
          or_pax: order.or_pax,
          or_waiter: order.or_waiter,
          od_kitchen: order.od_kitchen,
          od_printer: order.od_printer,
          or_readyid: order.or_readyid,
          or_ready: order.or_ready,
          or_ordertime: order.or_ordertime
            ? moment(order.or_ordertime).format('HH:mm') +
              ' - ' +
              moment(order.or_ordertime).diff(
                moment(order.or_date),
                'minutes',
              ) +
              'Mins'
            : '',
          or_readytime:
            order.or_ready == '2'
              ? moment(order.or_readytime).format('HH:mm') +
                ' - ' +
                moment(order.or_readytime).diff(
                  moment(order.or_date),
                  'minutes',
                ) +
                'Mins'
              : '',
        },
        items: [],
      };
    }
    grouped[headerKey].items.push({
      od_srid: order.od_srid,
      od_prod: order.od_prod,
      od_descr: order.od_descr,
      od_instruct: order.od_instruct,
      od_qty: order.od_qty,
    });
  });

  return Object.values(grouped);
};

const POSRunningOrders: React.FC = () => {
  const [isSaving, setIsSaving] = useState(false);
  const {...methods} = useForm({
    defaultValues: {
      selectedKitchen: '',
    },
  });
  const selectedKitchen = methods.watch('selectedKitchen');
  const gdata: any = useSelector((state: RootState) => state.gdata);
  const cid = gdata.company;

  const {data: kitchenData} = useGetPOMKitchensQuery(cid);

  const {
    data: orders,
    isLoading,
    isError,
  } = useGetPORunningOrdersQuery(cid);

  const [InsertPOEOrderReady] = useInsertPOEOrderReadyMutation();
  const [UpdatePOEOrderReady] = useUpdatePOEOrderReadyMutation();

  const kitchens = useMemo(
    () =>
      kitchenData?.map(kitchen => ({
        label: kitchen.kt_name,
        id: kitchen.kt_name,
        value: kitchen,
      })),
    [kitchenData],
  );

  const groupedOrders = useMemo(() => {
    const allGroupedOrders = groupOrders(orders || []);
    if (!selectedKitchen) return allGroupedOrders;
    return allGroupedOrders.filter(
      order => order.header.od_kitchen === selectedKitchen,
    );
  }, [orders, selectedKitchen]);

  if (isLoading) {
    return <Text><T>Loading...</T></Text>;
  }

  if (isError) {
    return <Text><T>Error fetching orders.</T></Text>;
  }

  return (
    <ScrollView
      style={styles.app}
      contentContainerStyle={{
        flexGrow: 1,
        paddingBottom: 80,
      }}
      keyboardShouldPersistTaps="handled">
      {/* Modern Header */}
      <View style={styles.modernHeaderContainer}>
        <Text style={styles.modernHeaderTitle}><T>Running Orders</T></Text>
      </View>

      <FormProvider {...methods}>
        <View style={styles.modernCard}>
          <BTComboBox
            name="selectedKitchen"
            label="Kitchen"
            data={kitchens}
            placeholder={tp("Select Kitchen")}
          />
        </View>
      </FormProvider>
      {groupedOrders.map((order, index) => (
        <View key={index} style={styles.modernCard}>
          <View
            style={
              !order.header.or_ready
                ? styles.headerContainerRed
                : order.header.or_ready == '1'
                ? styles.headerContainerBlue
                : styles.headerContainerGreen
            }>
            <View style={styles.headerCenter}>
              <Text
                style={styles.headerText}>{`${order.header.od_kitchen}`}</Text>
            </View>
          </View>
          <View style={styles.headerContainer}>
            <View style={styles.headerLeft}>
              <Text style={styles.headerText}>{`#${order.header.or_no}`}</Text>
            </View>
            <View style={styles.headerRight}>
              <Text
                style={styles.headerText}>{`Dt. ${order.header.or_date}`}</Text>
            </View>
          </View>
          <View style={styles.headerContainer}>
            <View style={styles.headerLeft}>
              <Text>{order.header.or_outlet}</Text>
            </View>
            <View style={styles.headerRight}>
              <Text>{order.header.or_area}</Text>
            </View>
          </View>
          <View style={styles.headerContainer}>
            <View style={styles.headerLeft}>
              <Text>{order.header.or_table}</Text>
            </View>
            <View style={styles.headerRight}>
              <Text>{order.header.or_waiter}</Text>
            </View>
          </View>
          <View style={styles.detailHeaderRow}>
            <Text style={styles.detailHeaderText}><T>Description</T></Text>
            <Text style={styles.detailHeaderText}><T>Qty</T></Text>
          </View>
          {order.items.map((item, itemIndex) => (
            <>
              <View key={itemIndex} style={styles.itemRow}>
                <Text style={styles.itemDescr}>{item.od_descr}</Text>
                <Text style={styles.itemQty}>{item.od_qty}</Text>
              </View>
              {item.od_instruct && item.od_instruct != '' ? (
                <View key={itemIndex} style={styles.itemRow}>
                  <Text style={styles.itemDescr}>{item.od_instruct}</Text>
                </View>
              ) : null}
            </>
          ))}
          <View style={styles.headerContainer}>
            <View style={styles.headerLeft}>
              <Text
                style={
                  styles.headerText
                }>{`Ord. ${order.header.or_ordertime}`}</Text>
            </View>
            <View style={styles.headerRight}>
              <Text
                style={
                  styles.headerText
                }>{`Rdy. ${order.header.or_readytime}`}</Text>
            </View>
          </View>
          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={styles.button}
              onPress={async () => {
                setIsSaving(true);
                if (Number(order.header.or_readyid) == 0) {
                  const values = {
                    rd_id: 0,
                    rd_date: moment().format('YYYY/MM/DD HH:mm:ss'),
                    rd_order: order.header.or_id,
                    rd_flag: '1',
                    rd_uid: gdata.user,
                    rd_cdt: moment().format('YYYY/MM/DD HH:mm:ss'),
                    rd_muid: gdata.user,
                    rd_mdt: moment().format('YYYY/MM/DD HH:mm:ss'),
                    rd_details: order.items.map((item, itemIndex) => {
                      return {
                        dt_id: 0,
                        dt_srid: itemIndex,
                        dt_prod: item.od_prod,
                        dt_descr: item.od_descr,
                        dt_instruct: item.od_instruct,
                        dt_qty: item.od_qty,
                      };
                    }),
                  };

                  try {
                    await InsertPOEOrderReady(values).unwrap();
                    ShowToast('Data Saved Sucessfully');
                  } catch (error: any) {
                    ShowToast(error?.data?.message || error?.message || 'Failed to update order status', 'error');
                  }
                } else {
                  const values = {
                    rd_id: Number(order.header.or_readyid),
                    rd_date: moment().format('YYYY/MM/DD HH:mm:ss'),
                    rd_order: order.header.or_id,
                    rd_flag: '2',
                    rd_uid: gdata.user,
                    rd_cdt: moment().format('YYYY/MM/DD HH:mm:ss'),
                    rd_muid: gdata.user,
                    rd_mdt: moment().format('YYYY/MM/DD HH:mm:ss'),
                  };

                  try {
                    await UpdatePOEOrderReady(values).unwrap();
                    ShowToast('Data Updated Sucessfully');
                  } catch (error: any) {
                    ShowToast(error?.data?.message || error?.message || 'Failed to update order status', 'error');
                  }
                }
                setIsSaving(false);
              }}
              disabled={order.header.or_ready == '2' || isSaving}>
              <Icon name="check" size={24} color="#4CAF50" />
              <Text style={styles.buttonText}>
                {!order.header.or_ready
                  ? 'Order'
                  : order.header.or_ready == '1'
                  ? 'Ready'
                  : 'Pickup'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.button}
              onPress={() => {
                // No kitchen-ticket reprint template exists for this screen
                // yet - previously this button silently did nothing, which
                // misled staff into thinking it worked. Surface that plainly
                // instead until real reprint support is built.
                ShowToast('Reprint is not available yet', 'info');
              }}>
              <Icon name="print" size={24} color="#2196F3" />
              <Text style={styles.buttonText}><T>Print</T></Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.button}
              onPress={() => {
                // Cancelling a KOT from here needs the same flow as
                // Screens/POSCancelKOT.tsx (which cancels by table, not by
                // order id) - not wired up yet. Surface that plainly instead
                // of silently doing nothing.
                ShowToast('Use Cancel KOT from the table screen to cancel this order', 'info');
              }}>
              <Icon name="close" size={24} color="#F44336" />
              <Text style={styles.buttonText}><T>Cancel</T></Text>
            </TouchableOpacity>
          </View>
        </View>
      ))}
    </ScrollView>
  );
};

export default POSRunningOrders;

